import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import './CustomsGuide.css';

const COMING_SOON = process.env.REACT_APP_COMING_SOON === 'true';

const SECTIONS = [
  {
    id: 'allowances', icon: '✅', color: 'green',
    titleKey: 'customs.s_allowances_title',
    items: [
      { labelKey: 'customs.s_allowances_1_label', detailKey: 'customs.s_allowances_1_detail' },
      { labelKey: 'customs.s_allowances_2_label', detailKey: 'customs.s_allowances_2_detail' },
      { labelKey: 'customs.s_allowances_3_label', detailKey: 'customs.s_allowances_3_detail' },
      { labelKey: 'customs.s_allowances_4_label', detailKey: 'customs.s_allowances_4_detail' },
      { labelKey: 'customs.s_allowances_5_label', detailKey: 'customs.s_allowances_5_detail' },
      { labelKey: 'customs.s_allowances_6_label', detailKey: 'customs.s_allowances_6_detail' },
      { labelKey: 'customs.s_allowances_7_label', detailKey: 'customs.s_allowances_7_detail' },
      { labelKey: 'customs.s_allowances_8_label', detailKey: 'customs.s_allowances_8_detail' },
    ],
  },
  {
    id: 'duties', icon: '💶', color: 'yellow',
    titleKey: 'customs.s_duties_title',
    items: [
      { labelKey: 'customs.s_duties_1_label', detailKey: 'customs.s_duties_1_detail' },
      { labelKey: 'customs.s_duties_2_label', detailKey: 'customs.s_duties_2_detail' },
      { labelKey: 'customs.s_duties_3_label', detailKey: 'customs.s_duties_3_detail' },
      { labelKey: 'customs.s_duties_4_label', detailKey: 'customs.s_duties_4_detail' },
      { labelKey: 'customs.s_duties_5_label', detailKey: 'customs.s_duties_5_detail' },
      { labelKey: 'customs.s_duties_6_label', detailKey: 'customs.s_duties_6_detail' },
    ],
  },
  {
    id: 'forbidden', icon: '🚫', color: 'red',
    titleKey: 'customs.s_forbidden_title',
    items: [
      { labelKey: 'customs.s_forbidden_1_label', detailKey: 'customs.s_forbidden_1_detail' },
      { labelKey: 'customs.s_forbidden_2_label', detailKey: 'customs.s_forbidden_2_detail' },
      { labelKey: 'customs.s_forbidden_3_label', detailKey: 'customs.s_forbidden_3_detail' },
      { labelKey: 'customs.s_forbidden_4_label', detailKey: 'customs.s_forbidden_4_detail' },
      { labelKey: 'customs.s_forbidden_5_label', detailKey: 'customs.s_forbidden_5_detail' },
      { labelKey: 'customs.s_forbidden_6_label', detailKey: 'customs.s_forbidden_6_detail' },
      { labelKey: 'customs.s_forbidden_7_label', detailKey: 'customs.s_forbidden_7_detail' },
    ],
  },
  {
    id: 'tips', icon: '💡', color: 'blue',
    titleKey: 'customs.s_tips_title',
    items: [
      { labelKey: 'customs.s_tips_1_label', detailKey: 'customs.s_tips_1_detail' },
      { labelKey: 'customs.s_tips_2_label', detailKey: 'customs.s_tips_2_detail' },
      { labelKey: 'customs.s_tips_3_label', detailKey: 'customs.s_tips_3_detail' },
      { labelKey: 'customs.s_tips_4_label', detailKey: 'customs.s_tips_4_detail' },
      { labelKey: 'customs.s_tips_5_label', detailKey: 'customs.s_tips_5_detail' },
      { labelKey: 'customs.s_tips_6_label', detailKey: 'customs.s_tips_6_detail' },
      { labelKey: 'customs.s_tips_7_label', detailKey: 'customs.s_tips_7_detail' },
    ],
  },
  {
    id: 'transporter', icon: '✈️', color: 'purple',
    titleKey: 'customs.s_transporter_title',
    items: [
      { labelKey: 'customs.s_transporter_1_label', detailKey: 'customs.s_transporter_1_detail' },
      { labelKey: 'customs.s_transporter_2_label', detailKey: 'customs.s_transporter_2_detail' },
      { labelKey: 'customs.s_transporter_3_label', detailKey: 'customs.s_transporter_3_detail' },
      { labelKey: 'customs.s_transporter_4_label', detailKey: 'customs.s_transporter_4_detail' },
      { labelKey: 'customs.s_transporter_5_label', detailKey: 'customs.s_transporter_5_detail' },
      { labelKey: 'customs.s_transporter_6_label', detailKey: 'customs.s_transporter_6_detail' },
    ],
  },
];

const COLOR_MAP = {
  green:  { bg: 'rgba(76,175,125,0.08)',  border: 'rgba(76,175,125,0.2)',  text: '#4CAF7D' },
  yellow: { bg: 'rgba(232,197,71,0.08)',  border: 'rgba(232,197,71,0.2)',  text: '#E8C547' },
  red:    { bg: 'rgba(232,52,26,0.08)',   border: 'rgba(232,52,26,0.2)',   text: '#E8341A' },
  blue:   { bg: 'rgba(52,152,219,0.08)',  border: 'rgba(52,152,219,0.2)',  text: '#5dade2' },
  purple: { bg: 'rgba(155,89,182,0.08)',  border: 'rgba(155,89,182,0.2)', text: '#a569bd' },
};

export default function CustomsGuide() {
  const { t } = useLang();
  const { profile } = useAuth();
  const [activeSection, setActiveSection] = useState(null);
  const [search, setSearch] = useState('');

  const hasAppAccess = profile?.is_admin || profile?.is_beta_tester;

  const filtered = SECTIONS.map(section => ({
    ...section,
    resolvedTitle: t(section.titleKey),
    items: section.items
      .map(item => ({ label: t(item.labelKey), detail: t(item.detailKey) }))
      .filter(item =>
        !search ||
        item.label.toLowerCase().includes(search.toLowerCase()) ||
        item.detail.toLowerCase().includes(search.toLowerCase())
      ),
  })).filter(s => !search || s.items.length > 0);

  return (
    <div className="page customs-page">
      <div className="container">

        {/* Header */}
        <div className="customs-header">
          {(!COMING_SOON || hasAppAccess)
            ? <Link to="/search" className="back-link">{t('customs.backToSearch')}</Link>
            : <Link to="/" className="back-link">← Home</Link>
          }
          <div className="customs-hero">
            <div className="customs-hero-icon">🛃</div>
            <div>
              <h1>{t('customs.title')}</h1>
              <p>{t('customs.subtitle')} — {t('customs.updated')}</p>
            </div>
          </div>
          <div className="customs-disclaimer">
            <span>⚠️</span>
            <span>{t('customs.disclaimer')}</span>
          </div>
        </div>

        {/* Search */}
        <div className="customs-search">
          <input
            type="text"
            placeholder={t('customs.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="customs-search-clear" onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        {/* Quick nav */}
        {!search && (
          <div className="customs-quicknav">
            {SECTIONS.map(s => {
              const c = COLOR_MAP[s.color];
              return (
                <button
                  key={s.id}
                  className="customs-quicknav-btn"
                  style={{ borderColor: c.border, color: c.text }}
                  onClick={() => {
                    setActiveSection(activeSection === s.id ? null : s.id);
                    document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {s.icon} {t(s.titleKey)}
                </button>
              );
            })}
          </div>
        )}

        {/* Sections */}
        <div className="customs-sections">
          {filtered.map(section => {
            const c = COLOR_MAP[section.color];
            return (
              <div key={section.id} id={`section-${section.id}`} className="customs-section" style={{ borderColor: c.border }}>
                <div className="customs-section-header" style={{ background: c.bg }}>
                  <span className="customs-section-icon">{section.icon}</span>
                  <h2 className="customs-section-title" style={{ color: c.text }}>{section.resolvedTitle}</h2>
                  <span className="customs-section-count" style={{ color: c.text }}>{section.items.length} {t('customs.items')}</span>
                </div>
                <div className="customs-items">
                  {section.items.map((item, i) => (
                    <div key={i} className="customs-item">
                      <div className="customs-item-label">
                        <span className="customs-item-dot" style={{ background: c.text }} />
                        {item.label}
                      </div>
                      <div className="customs-item-detail">{item.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="customs-empty">
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
              <div style={{ fontWeight: '700', color: 'var(--sand)', marginBottom: '6px' }}>{t('customs.noResults')} "{search}"</div>
              <div style={{ fontSize: '14px' }}>{t('customs.noResultsTip')}</div>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="customs-cta">
          <div className="customs-cta-inner">
            <div>
              <div style={{ fontFamily: 'Syne', fontSize: '18px', fontWeight: '700', marginBottom: '6px' }}>{t('customs.ctaTitle')}</div>
              <div style={{ color: 'var(--olive)', fontSize: '14px' }}>{t('customs.ctaSubtitle')}</div>
            </div>
            {COMING_SOON && !hasAppAccess
              ? <a href="/#signup" className="btn btn-primary">{t('customs.joinWaitlist')}</a>
              : <Link to="/search" className="btn btn-primary">{t('customs.browseTrips')}</Link>
            }
          </div>
        </div>

      </div>
    </div>
  );
}